import { Box, Button, Link, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useState, useRef, useMemo, useCallback } from 'react'
import useFormValidation from '../../utils/hooks/useFormValidation'
import ErrorDisplay from './ErrorDisplay'
import FormRenderer from './FormRenderer'
import RecaptchaWrapper from './RecaptchaWrapper'

const BLUE_BASE = '#092A48'
const WHITE = '#FFFFFF'

const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
}))

const FormBody = styled('form')(({ theme }) => ({
  width: '100%',
}))

const FormSuccess = styled(Typography)(({ theme }) => ({
  color: BLUE_BASE,
  marginBottom: theme.spacing(2.5),
}))

const GetHelp = styled(Typography)(({ theme }) => ({
  color: 'red',
  margin: theme.spacing(2.5, 0),
  '& a': {
    color: BLUE_BASE,
    fontWeight: 'bold',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}))

const SubmitButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'buttonStyles',
})(({ buttonStyles, theme }) => ({
  backgroundColor: BLUE_BASE,
  border: 'none',
  fontSize: '14px',
  borderRadius: '25px',
  padding: theme.spacing(1.25, 2.5),
  color: WHITE,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  cursor: 'pointer',
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(5),
  textTransform: 'none',
  '&:disabled': {
    backgroundColor: '#DDE5ED',
    color: '#09482A',
    cursor: 'not-allowed',
  },
  ...buttonStyles,
}))

const CustomForm = ({
  formId,
  formFields = [],
  formHTML = '',
  isModal = false,
  ctaText = 'Submit',
  onSubmit = null,
  thanksMessage: customThanksMessage = '',
  formCSRF,
  initialValues,
  buttonStyles,
  formStyles,
}) => {
  const [formValues, setFormValues] = useState(initialValues || {})
  const [fieldErrors, setFieldErrors] = useState({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [thanksMessage, setThanksMessage] = useState('')
  const fieldRefs = useRef({})

  const isNetlifyPreview = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.location.hostname.includes('hca-dev.netlify.app')
    }
    return false
  }, [])

  const { validateField, validateAllFields, clearErrors } = useFormValidation(
    formHTML,
    formFields,
    formValues
  )

  const [recaptchaToken, setRecaptchaToken] = useState('')
  const memoizedFormFields = useMemo(() => formFields, [formFields])

  const handleInputChange = useCallback(
    (e) => {
      const { id, value } = e.target
      setFormValues((prevValues) => ({
        ...prevValues,
        [id]: value,
      }))
      validateField(id, value)
    },
    [validateField]
  )

  const handleRecaptchaChange = useCallback((value) => {
    setRecaptchaToken(value)
  }, [])

  const handleRecaptchaExpire = useCallback(() => {
    setRecaptchaToken('')
  }, [])

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      setErrorMessage('')
      clearErrors()
      const { hasError, newFieldErrors } = validateAllFields()
      if (hasError) {
        setFieldErrors(newFieldErrors)
        setErrorMessage('Please fix the errors in the form.')
        return
      }
      if (!isNetlifyPreview && !recaptchaToken) {
        setErrorMessage('Please complete the reCAPTCHA.')
        return
      }
      if (!formCSRF) {
        setErrorMessage('CSRF token is missing.')
        return
      }
      setIsSubmitting(true)
      try {
        let submitResult
        if (onSubmit) {
          submitResult = await onSubmit({
            ...formValues,
            formCSRF,
            recaptchaToken,
          })
        }
        setFormSubmitted(true)
        setThanksMessage(
          customThanksMessage ||
            submitResult?.thanksMessage ||
            'Thank you for your submission.'
        )
      } catch (error) {
        setErrorMessage(
          error.message ||
            'There was an error submitting the form. Please try again.'
        )
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      clearErrors,
      customThanksMessage,
      formValues,
      onSubmit,
      recaptchaToken,
      validateAllFields,
      formCSRF,
      isNetlifyPreview,
    ]
  )

  return (
    <FormContainer>
      {!formSubmitted ? (
        <FormBody
          onSubmit={handleSubmit}
          noValidate
          aria-label="CIT Login Form"
        >
          <FormRenderer
            formFields={memoizedFormFields}
            formValues={formValues}
            handleInputChange={handleInputChange}
            fieldErrors={fieldErrors}
            fieldRefs={fieldRefs}
            formHTML={formHTML}
            formStyles={formStyles}
          />
          {!isNetlifyPreview && (
            <RecaptchaWrapper
              formId={formId}
              gatsbyRecaptchaKey={process.env.GATSBY_RECAPTCHA_KEY || ''}
              onRecaptchaChange={handleRecaptchaChange}
              onRecaptchaExpire={handleRecaptchaExpire}
              formSubmitted={formSubmitted}
            />
          )}
          {errorMessage && (
            <>
              <ErrorDisplay
                errorMessage={errorMessage}
                fieldErrors={fieldErrors}
              />
              <GetHelp>
                Looking for additional support?{' '}
                <Link href="mailto:WebTeam@harborcapital.com">Click here</Link>{' '}
                to get in touch.
              </GetHelp>
            </>
          )}
          <SubmitButton
            type="submit"
            disabled={
              isSubmitting ||
              (!isNetlifyPreview && !recaptchaToken) ||
              !formCSRF
            }
            aria-disabled={
              isSubmitting ||
              (!isNetlifyPreview && !recaptchaToken) ||
              !formCSRF
            }
            aria-busy={isSubmitting}
            aria-label="Submit Login Form"
            buttonStyles={buttonStyles}
          >
            {isSubmitting ? 'Submitting...' : ctaText}
          </SubmitButton>
        </FormBody>
      ) : (
        <FormSuccess role="status" aria-live="polite">
          {thanksMessage}
        </FormSuccess>
      )}
    </FormContainer>
  )
}

CustomForm.propTypes = {
  formId: PropTypes.string.isRequired,
  formFields: PropTypes.array,
  formHTML: PropTypes.string,
  isModal: PropTypes.bool,
  ctaText: PropTypes.string,
  onSubmit: PropTypes.func,
  thanksMessage: PropTypes.string,
  formCSRF: PropTypes.string.isRequired,
  buttonStyles: PropTypes.any,
  formStyles: PropTypes.any,
  initialValues: PropTypes.object,
}

export default CustomForm
